import type { KeywordListType } from "app/modules/demand-analysis/keyword-lists";
import { createKeywordListEntity } from "app/modules/demand-analysis/keyword-lists";

import type { MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode as DemandSearchTrendsKeywords } from "@similarweb/pro-api-client";

/**
 * The Table component in Pro Logic Components uses the `dataIndex` property to retrieve cell data from a row and as a sorting key.
 * If the user exports data to Excel, we use this sorting key in the `sort` query parameter. Therefore, we must use the same names as the backend.
 * Check out the `GetExcelTableRows` method implementation to see what names are used.
 * @see {@link SimilarWebPro.Website.Features.DemandSearchTrends.Services.SearchTrendsKeywordGroupsAnalysisService.GetExcelTableRows}
 */
interface KeywordListsComparisonSortableFields {
    keywordGroupName: string;
    monthlyVolume: number;
    volumeChange: number;
    volumeShare: number;
}

export interface KeywordListsComparisonTableRow extends KeywordListsComparisonSortableFields {
    id: string;
    type: KeywordListType;
    keywordListId: string;
    previousVolume?: number;
    previousShare?: number;
}

export const processSearchTrendsKeywordsData = (
    result: DemandSearchTrendsKeywords,
): KeywordListsComparisonTableRow[] => {
    if (!result.children) {
        return [];
    }

    const rows: KeywordListsComparisonTableRow[] = result.children
        .map((child) => {
            const keywordListEntity = createKeywordListEntity(child);

            if (!keywordListEntity) {
                return undefined;
            }

            if (child.previousPeriod !== undefined) {
                return {
                    id: keywordListEntity.id,
                    type: keywordListEntity.type,
                    keywordListId: keywordListEntity.keywordListId,
                    keywordGroupName: keywordListEntity.name,
                    monthlyVolume: child.currentPeriod.averageVolume,
                    volumeChange: child.currentPeriod.periodVolumeChange?.change ?? 0,
                    volumeShare: child.currentPeriod.volumeShare,
                    previousVolume: child.previousPeriod.averageVolume,
                    previousShare: child.previousPeriod.volumeShare,
                };
            }

            return {
                id: keywordListEntity.id,
                type: keywordListEntity.type,
                keywordListId: keywordListEntity.keywordListId,
                keywordGroupName: keywordListEntity.name,
                monthlyVolume: child.currentPeriod.averageVolume,
                volumeChange: child.currentPeriod.periodVolumeChange?.change ?? 0,
                volumeShare: child.currentPeriod.volumeShare,
            };
        })
        .filter((row) => row !== undefined);

    return rows;
};
